import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Shared modal behaviour: locks background scroll, traps Tab focus inside the
// modal, closes on Escape (via the caller's own guard), and restores focus to
// whatever triggered the modal once it unmounts. Used by both
// RegisterMemberModal and CollectPaymentModal so the two stay consistent.
export default function useModalA11y({ isOpen, onEscape, containerRef, restoreFocusRef }) {
  const onEscapeRef = useRef(onEscape);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const restoreTarget = restoreFocusRef?.current;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFirst = () => {
      const node = containerRef.current;
      if (!node) return;
      const focusables = node.querySelectorAll(FOCUSABLE_SELECTOR);
      (focusables[0] || node).focus();
    };
    focusFirst();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onEscapeRef.current?.(e);
        return;
      }

      if (e.key !== 'Tab') return;

      const node = containerRef.current;
      if (!node) return;

      const focusables = Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;

      const target = restoreTarget || previouslyFocused;
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
}
